import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { BellCheck } from '@components/EmailNotifications/GetNotifiedButton/BellCheck';
import { useGetNotified } from '@components/EmailNotifications/useGetNotified';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { SquareImage } from '@components/main-components/image/BaseImage';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { useAuth } from '@context/auth/base';
import {
  type FragmentType,
  getFragment,
  gql,
  MintComponentSongInfoFragmentDoc,
} from '@graphql/reactQuery';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment MintComponentSongInfo on Release {
    id
    title
    webappUri
    staticCoverImage {
      id
      url
    }
    coverImage {
      id
      url
    }
    artist {
      id
      userId
      webappUri
      name
    }
  }
`);

export const SongInfo = ({
  release,
}: {
  release: FragmentType<MintComponentSongInfoFragmentDoc>;
}) => {
  const { authUser } = useAuth();

  const { title, staticCoverImage, coverImage, webappUri, artist } = getFragment(
    MintComponentSongInfoFragmentDoc,
    release,
  );

  const isOwner = artist.userId === authUser?.id;
  const showSubscibeIcon = !isOwner && !!artist.id && !!artist.userId;

  const { loading, showSubscribed, handleClick } = useGetNotified({
    artistId: artist.id,
    userId: artist.userId,
  });

  const subscribeIcon = showSubscribed ? (
    <span className="[&_svg]:h-6">
      <BellCheck />
    </span>
  ) : (
    <FontAwesomeIcon icon={faBell} />
  );
  return (
    <SongInfoComponent
      title={title}
      artistName={artist.name}
      songWebappUri={webappUri}
      artistWebappUri={artist.webappUri}
      coverImage={staticCoverImage?.url || coverImage.url}
      iconButton={
        showSubscibeIcon ? (
          <IconButton
            variant="tertiary"
            disabled={loading}
            onClick={handleClick}
            iconSize={showSubscribed ? 20 : 18}
            icon={subscribeIcon}
          />
        ) : null
      }
    />
  );
};

export const SongInfoComponent = ({
  title,
  coverImage,
  songWebappUri,
  artistWebappUri,
  artistName,
  iconButton,
}: {
  title: string;
  coverImage: string;
  songWebappUri?: string;
  artistWebappUri?: string;
  artistName: string;
  iconButton: React.ReactNode | null;
}) => {
  const songTitle = (
    <h4 className="overflow-hidden text-ellipsis font-title text-title-xs font-medium text-base800">
      {title}
    </h4>
  );

  const artist = (
    <h5 className="overflow-hidden text-ellipsis text-kicker-xs tracking-normal text-base500 ">
      {artistName}
    </h5>
  );
  return (
    <div className="flex w-full items-center justify-between gap-2">
      <div className="flex w-full items-center overflow-hidden">
        <div className="flex-shrink-0">
          <SquareImage alt={title} src={coverImage} size={48} borderRadius={8} />
        </div>
        <div className="ml-3 flex flex-col justify-center gap-[6px] overflow-hidden whitespace-nowrap">
          {songWebappUri ? (
            <AccessibleLink pathnameUri={webappUriPath(songWebappUri)}>{songTitle}</AccessibleLink>
          ) : (
            songTitle
          )}
          {artistWebappUri ? (
            <AccessibleLink
              pathnameUri={webappUriPath(artistWebappUri)}
              className="hover:cursor-pointer"
            >
              {artist}
            </AccessibleLink>
          ) : (
            artist
          )}
        </div>
      </div>

      {iconButton}
    </div>
  );
};
